import { useMemo } from 'react';

const dots = '...';

const range = (start: number, end: number): number[] => {
    const length = end - start + 1;

    /* eslint-disable-next-line @typescript-eslint/naming-convention */
    return Array.from({ length }, (_, idx) => idx + start);
};

export const usePagination = (
    count: number,
    limit: number,
    siblingCount = 1,
    page: number
): (string | number)[] => {
    return useMemo((): (string | number)[] => {
        const countPageCount = Math.ceil(count / limit);

        /* Pages count is determined as siblingCount + firstPage + lastPage + page + 2 * dots */
        const maxPageCount = siblingCount + 5;

        /*
          If the number of pages is less than the page numbers we want to show paginationComponent,
          return the range [1..countPageCount]
        */
        if (maxPageCount >= countPageCount) {
            return range(1, countPageCount);
        }

        const leftSiblingIndex = Math.max(page - siblingCount, 1);
        const rightSiblingIndex = Math.min(
            page + siblingCount,
            countPageCount
        );

        /*
          We do not want to show dots if there is only one position left
          after/before the left/right page count as that would lead to a change if our Pagination
          component size which we do not want
        */
        const showLeftDots = leftSiblingIndex > 3;
        const showRightDots = rightSiblingIndex < countPageCount - 2;

        const firstPageIndex = 1;
        const lastPageIndex = countPageCount;

        if (!showLeftDots && showRightDots) {
            const leftItemCount = 3 + 2 * siblingCount;
            const leftRange = range(1, leftItemCount);

            return [...leftRange, dots, countPageCount];
        }

        if (showLeftDots && !showRightDots) {
            const rightItemCount = 3 + 2 * siblingCount;
            const rightRange = range(
                countPageCount - rightItemCount + 1,
                countPageCount
            );

            return [firstPageIndex, dots, ...rightRange];
        }

        const middleRange = range(leftSiblingIndex, rightSiblingIndex);

        return [firstPageIndex, dots, ...middleRange, dots, lastPageIndex];
    }, [count, limit, siblingCount, page]);
};
